import { ApiError, type ApiClient, type ExportResult, type Unsubscribe } from './client';
import type {
  AuditEvent,
  AuditOutcome,
  CurrentUser,
  DeviceGroup,
  Invitation,
  Member,
  Membership,
  OrganizationSettings,
  PaymentMethod,
  Permission,
  PlanDefinition,
  Policy,
  PresenceEvent,
  Release,
  Role,
} from './types';
import { createSettingsHttp } from './settingsHttp';
import { useLocaleStore } from '../i18n';

export interface HttpClientOptions {
  baseUrl: string;
  /** Identité (comptes, mots de passe, 2FA) : Worker siraj-backend-apps, séparé de la console. */
  authBaseUrl: string;
  /** Fournit le jeton d'accès courant (lu à chaque requête). */
  getToken: () => string | null;
  /** Appelé sur 401 pour déconnecter l'utilisateur. */
  onUnauthorized?: () => void;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: 'Bearer';
  expires_in: number;
  account_id: string;
}

interface AccountMe {
  id: string;
  email: string;
  display_name?: string;
  totp_enabled: boolean;
}

interface ConsoleOrgSummary {
  id: string;
  name: string;
  createdAt: string;
}

interface ConsoleOrgDetail extends ConsoleOrgSummary {
  role: string;
  permissions: string[];
}

/** Forme brute de `GET /members` côté `siraj-backend-console` : nom et e-mail résolus par le serveur (`null` si inconnus) ; pas encore de 2FA. */
interface ConsoleMemberRaw {
  orgId: string;
  userId: string;
  role: string;
  addedAt: string;
  name?: string | null;
  email?: string | null;
}

function mapMember(raw: ConsoleMemberRaw): Member {
  return {
    id: raw.userId,
    email: raw.email ?? '',
    name: raw.name ?? raw.email ?? raw.userId,
    roleId: raw.role,
    groupIds: [],
    status: 'active',
    twoFactorEnabled: false,
    lastSeenAt: null,
    createdAt: raw.addedAt,
  };
}

/** Forme brute de `GET /audit` côté `siraj-backend-console` : identifiants bruts, plus noms résolus par le serveur (`null` si inconnus). */
interface ConsoleAuditEventRaw {
  id: string;
  at: string;
  actorId: string;
  actorName?: string | null;
  actorEmail?: string | null;
  /** `member`, `policy`, `group`, `device`, `invitation` ou `org` ; `null` si la cible n'est pas typable. */
  targetType?: string | null;
  targetName?: string | null;
  action: string;
  target?: string | null;
  outcome: string;
  ip?: string;
  meta?: Record<string, string | number | boolean>;
}

function mapAuditOutcome(raw: string): AuditOutcome {
  if (raw === 'ok') return 'success';
  if (raw === 'denied') return 'denied';
  return 'failure';
}

function mapAuditEvent(raw: ConsoleAuditEventRaw): AuditEvent {
  return {
    id: raw.id,
    at: raw.at,
    actor: { type: 'member', id: raw.actorId, name: raw.actorName ?? raw.actorEmail ?? raw.actorId, ...(raw.actorEmail ? { email: raw.actorEmail } : {}) },
    action: raw.action,
    target: raw.target ? { type: raw.targetType ?? 'resource', id: raw.target, name: raw.targetName ?? raw.target } : undefined,
    outcome: mapAuditOutcome(raw.outcome),
    ip: raw.ip,
    // Pas encore de vérification de chaîne de hash côté client ; le serveur signe (`prevHash`/`hash`).
    signatureValid: true,
    metadata: raw.meta,
  };
}

/**
 * `siraj-backend-console` ne renvoie que `{ orgId, name, timezone, defaultLocale }` : les champs de sécurité,
 * de marque blanche et de rétention sont complétés par des valeurs par défaut pour que l'IHM ne plante pas.
 */
export function normalizeOrgSettings(org: string, raw: Partial<OrganizationSettings> & { orgId?: string }): OrganizationSettings {
  return {
    id: raw.id ?? raw.orgId ?? org,
    name: raw.name ?? '',
    slug: raw.slug ?? raw.orgId ?? org,
    plan: raw.plan ?? 'free',
    createdAt: raw.createdAt ?? '',
    region: raw.region ?? 'eu',
    domain: raw.domain,
    require2fa: raw.require2fa ?? false,
    whiteLabel: { enabled: false, primaryColor: '#0ea5e9', ...raw.whiteLabel },
    timezone: raw.timezone,
    defaultLocale: raw.defaultLocale,
    recordingRetentionDays: raw.recordingRetentionDays,
    security: { sessionTimeoutMin: 60, allowedCountries: [], blockedIps: [], trustedDevicesOnly: false, ...raw.security },
    auditRetentionDays: raw.auditRetentionDays ?? 90,
  };
}

/** Forme brute d'un plan de `GET /v1/plans` (snake_case). */
interface ConsolePlanRaw {
  id: string;
  name: string;
  description?: string;
  price_monthly_cents: number | null;
  quotas: { technicians: number; devices: number };
  features: string[];
  sort_order: number;
  active: boolean;
}

export function mapPlan(raw: ConsolePlanRaw): PlanDefinition {
  return {
    id: raw.id,
    name: raw.name,
    description: raw.description ?? '',
    priceMonthlyCents: raw.price_monthly_cents,
    quotas: raw.quotas,
    features: raw.features,
    sortOrder: raw.sort_order,
    active: raw.active,
  };
}

type Query = Record<string, string | number | boolean | undefined | null>;

function qs(params?: object): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Query)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Lit le corps d'une réponse réussie en JSON. Un corps non JSON (ex. page HTML d'un repli SPA ou d'un proxy)
 * devient une `ApiError` typée `invalid_response` au lieu d'une `SyntaxError` brute.
 */
async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError(res.status, 'invalid_response', 'Réponse non JSON du serveur');
  }
}

/** Implémentation REST de l'ApiClient (endpoints décrits dans openapi-notes.md). */
export function createHttpApiClient(opts: HttpClientOptions): ApiClient {
  const base = opts.baseUrl.replace(/\/$/, '');
  // `siraj-backend-apps` préfixe toutes ses routes par sa version d'API (`API_PREFIX` = `/v${API_VERSION}`,
  // actuellement `/v0`) ; `siraj-backend-console`, lui, a un préfixe `/v1` fixe déjà inclus dans les chemins ci-dessous.
  const authBase = opts.authBaseUrl.replace(/\/$/, '') + '/v0';

  async function rawAt(root: string, method: string, path: string, body: unknown, token: string | null, accept = 'application/json'): Promise<Response> {
    const res = await fetch(`${root}${path}`, {
      method,
      headers: {
        Accept: accept,
        'Accept-Language': useLocaleStore.getState().locale,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      if (res.status === 401 && root === base) opts.onUnauthorized?.();
      let code = 'http_error';
      let message: string | undefined;
      try {
        const data = (await res.json()) as { code?: string; message?: string };
        code = data.code ?? code;
        message = data.message;
      } catch {
        /* corps non JSON */
      }
      throw new ApiError(res.status, code, message);
    }
    return res;
  }

  async function raw(method: string, path: string, body?: unknown, accept = 'application/json'): Promise<Response> {
    return rawAt(base, method, path, body, opts.getToken(), accept);
  }

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await raw(method, path, body);
    if (res.status === 204) return undefined as T;
    return parseJson<T>(res);
  }

  /** Appel console avec un jeton explicite (amorçage login/register, avant que la session soit stockée). */
  async function callConsoleWithToken<T>(method: string, path: string, token: string, body?: unknown): Promise<T> {
    const res = await rawAt(base, method, path, body, token);
    if (res.status === 204) return undefined as T;
    return parseJson<T>(res);
  }

  /** Appel vers l'identité (`siraj-backend-apps`), public ou avec jeton explicite. */
  async function authCall<T>(method: string, path: string, body?: unknown, token: string | null = null): Promise<T> {
    const res = await rawAt(authBase, method, path, body, token);
    if (res.status === 204) return undefined as T;
    return parseJson<T>(res);
  }

  const o = (org: string) => `/v1/orgs/${encodeURIComponent(org)}`;

  /**
   * Compose le `CurrentUser` à partir des deux Workers : profil chez `apps` (identité),
   * organisations/rôle/permissions chez `console` (propres à cette organisation). Crée
   * l'organisation de la console si elle n'existe pas encore (ex. juste après `/auth/register`,
   * qui ne connaît que l'organisation « Personal » d'apps, invisible à la console).
   */
  async function resolveCurrentUser(token: string, newOrgName?: string): Promise<CurrentUser> {
    const me = await authCall<AccountMe>('GET', '/accounts/me', undefined, token);
    // La console ne reçoit ni nom ni e-mail dans le jeton : on les lui confie pour qu'elle les résolve dans
    // les listes (membres, invitations, politiques, audit). Échec sans conséquence : les identifiants restent affichés.
    await callConsoleWithToken('PUT', '/v1/me/profile', token, { name: me.display_name ?? undefined, email: me.email }).catch(() => undefined);
    let orgs = (await callConsoleWithToken<{ items: ConsoleOrgSummary[] }>('GET', '/v1/orgs', token)).items;
    if (orgs.length === 0) {
      // Compte sans miroir console (inscrit hors du front, ou premier accès) : le backend reprend l'id de l'organisation
      // apps et crée l'organisation au plan free (sans essai : la console exige ensuite un choix d'offre). En cas d'échec, on laisse la liste vide (LoginPage affiche `noOrg`).
      try {
        orgs = [await callConsoleWithToken<ConsoleOrgSummary>('POST', '/v1/orgs', token, { name: newOrgName || 'Personal' })];
      } catch {
        orgs = [];
      }
    }
    const memberships: Membership[] = await Promise.all(
      orgs.map(async (org) => {
        const detail = await callConsoleWithToken<ConsoleOrgDetail>('GET', o(org.id), token);
        // Permissions alignées avec `siraj-backend-console` pour les zones qu'il implémente
        // (orgs/membres/licence→billing/politiques/audit). Le parc, les sessions, le déploiement,
        // les intégrations et les clés API restent en mock faute de routes serveur.
        return { orgId: org.id, orgName: org.name, roleId: detail.role, roleName: detail.role, permissions: detail.permissions as Permission[] };
      }),
    );
    return { id: me.id, email: me.email, name: me.display_name ?? me.email, memberships, twoFactorEnabled: me.totp_enabled };
  }

  return {
    ...createSettingsHttp(call, o),
    register: async ({ email, password, name, orgName }) => {
      const tokens = await authCall<AuthTokens>('POST', '/auth/register', { email, password, display_name: name, locale: 'fr' });
      const user = await resolveCurrentUser(tokens.access_token, orgName);
      return { status: 'ok', token: tokens.access_token, user };
    },
    login: async ({ email, password }) => {
      const res = await authCall<{ mfa_required: boolean; mfa_token?: string; tokens?: AuthTokens }>('POST', '/auth/login', { email, password });
      if (res.mfa_required) return { status: 'totp_required', challengeId: res.mfa_token! };
      const user = await resolveCurrentUser(res.tokens!.access_token);
      return { status: 'ok', token: res.tokens!.access_token, user };
    },
    verifyTotp: async ({ challengeId, code }) => {
      const res = await authCall<{ verified: true; tokens: AuthTokens }>('POST', '/auth/totp/verify', { mfa_token: challengeId, code });
      const user = await resolveCurrentUser(res.tokens.access_token);
      return { token: res.tokens.access_token, user };
    },
    // Pas de révocation serveur : le jeton de rafraîchissement n'est pas conservé côté client (session access-token seul).
    logout: () => Promise.resolve(),
    me: async () => {
      const token = opts.getToken();
      if (!token) throw new ApiError(401, 'unauthenticated');
      return resolveCurrentUser(token);
    },

    listOrganizations: () => call('GET', '/v1/orgs'),
    getOrganizationSettings: async (org) => normalizeOrgSettings(org, await call('GET', `${o(org)}/settings`)),
    updateOrganizationSettings: async (org, patch) => normalizeOrgSettings(org, await call('PATCH', `${o(org)}/settings`, patch)),

    getDashboard: (org) => call('GET', `${o(org)}/dashboard`),

    listDevices: (org, p) => call('GET', `${o(org)}/devices${qs(p)}`),
    getDevice: (org, id) => call('GET', `${o(org)}/devices/${id}`),
    updateDevice: (org, id, patch) => call('PATCH', `${o(org)}/devices/${id}`, patch),
    revokeDevice: (org, id) => call('POST', `${o(org)}/devices/${id}/revoke`),
    requestDeviceUpdate: (org, id) => call('POST', `${o(org)}/devices/${id}/update-agent`),
    getAlertSettings: (org) => call('GET', `${o(org)}/alert-settings`),
    updateAlertSettings: (org, patch) => call('PATCH', `${o(org)}/alert-settings`, patch),
    getSsoConfig: (org) => call('GET', `${o(org)}/sso`),
    saveSsoConfig: (org, config) => call('PUT', `${o(org)}/sso`, config),
    listDeviceGroups: async (org) => (await call<{ items: DeviceGroup[] }>('GET', `${o(org)}/groups`)).items,
    createDeviceGroup: (org, input) => call('POST', `${o(org)}/groups`, input),
    updateDeviceGroup: (org, id, patch) => call('PATCH', `${o(org)}/groups/${id}`, patch),
    deleteDeviceGroup: (org, id) => call('DELETE', `${o(org)}/groups/${id}`),
    subscribePresence(org, handler): Unsubscribe {
      // Flux temps réel : WebSocket (le jeton passe en sous-protocole pour ne pas apparaître dans l'URL)
      const url = `${base.replace(/^http/, 'ws')}${o(org)}/events`;
      const token = opts.getToken();
      let closed = false;
      let ws: WebSocket | null = null;
      let retry = 1000;
      const connect = () => {
        if (closed) return;
        ws = token ? new WebSocket(url, ['siraj.v1', `bearer.${token}`]) : new WebSocket(url, ['siraj.v1']);
        ws.onmessage = (m) => {
          try {
            const msg = JSON.parse(String(m.data)) as { type: string; events?: PresenceEvent[] };
            if (msg.type === 'presence' && msg.events) handler(msg.events);
          } catch {
            /* message ignoré */
          }
        };
        ws.onopen = () => (retry = 1000);
        ws.onclose = () => {
          if (closed) return;
          setTimeout(connect, retry);
          retry = Math.min(retry * 2, 30000);
        };
      };
      connect();
      return () => {
        closed = true;
        ws?.close();
      };
    },

    listMembers: async (org, p) => {
      const res = await call<{ items: ConsoleMemberRaw[]; total?: number }>('GET', `${o(org)}/members${qs(p)}`);
      const items = res.items.map(mapMember);
      return { items, total: res.total ?? items.length, page: p?.page ?? 1, pageSize: p?.pageSize ?? items.length };
    },
    updateMember: (org, id, patch) => call('PATCH', `${o(org)}/members/${id}`, patch),
    removeMember: (org, id) => call('DELETE', `${o(org)}/members/${id}`),
    listRoles: async (org) => (await call<{ items: Role[] }>('GET', `${o(org)}/roles`)).items,
    saveRole: (org, role) =>
      role.id ? call('PUT', `${o(org)}/roles/${role.id}`, role) : call('POST', `${o(org)}/roles`, role),
    deleteRole: (org, id) => call('DELETE', `${o(org)}/roles/${id}`),
    listInvitations: async (org) => (await call<{ items: Invitation[] }>('GET', `${o(org)}/invitations`)).items,
    createInvitation: (org, req) => call('POST', `${o(org)}/invitations`, req),
    revokeInvitation: (org, id) => call('DELETE', `${o(org)}/invitations/${id}`),
    resendInvitation: (org, id) => call('POST', `${o(org)}/invitations/${id}/resend`),
    acceptInvitation: async (org, id) => {
      await call('POST', `/v1/invitations/${encodeURIComponent(org)}/${encodeURIComponent(id)}/accept`);
    },

    listPolicies: async (org) => (await call<{ items: Policy[] }>('GET', `${o(org)}/policies`)).items,
    getPolicy: (org, id) => call('GET', `${o(org)}/policies/${id}`),
    createPolicy: (org, input) => call('POST', `${o(org)}/policies`, input),
    updatePolicy: (org, id, input) => call('PATCH', `${o(org)}/policies/${id}`, input),
    deletePolicy: (org, id) => call('DELETE', `${o(org)}/policies/${id}`),
    previewPolicy: (org, input) => call('POST', `${o(org)}/policies/preview`, input),
    listPolicyVersions: (org, id) => call('GET', `${o(org)}/policies/${id}/versions`),
    rollbackPolicy: (org, id, version) => call('POST', `${o(org)}/policies/${id}/rollback`, { version }),
    getPolicyDelivery: (org, id) => call('GET', `${o(org)}/policies/${id}/delivery`),

    listAuditEvents: async (org, p) => {
      // `siraj-backend-console` ne connaît que actor_id/action/from/to/limit (pas de page, de total ni de filtres
      // outcome/q/appareil/signature) : on lit la fenêtre maximale puis on filtre, trie et pagine côté client.
      const { page = 1, pageSize = 100, q, outcome, signature, deviceId, sort, direction, actorId, ...rest } = p ?? {};
      const res = await call<{ items: ConsoleAuditEventRaw[] }>('GET', `${o(org)}/audit${qs({ ...rest, actor_id: actorId, limit: 500 })}`);
      const needle = q?.trim().toLowerCase();
      let items = res.items.map(mapAuditEvent).filter(
        (e) =>
          (!outcome || e.outcome === outcome) &&
          (!signature || e.signatureValid === (signature === 'valid')) &&
          (!deviceId || e.target?.id === deviceId) &&
          (!needle || [e.actor.name, e.action, e.target?.name, e.ip].some((v) => v?.toLowerCase().includes(needle))),
      );
      const key = { at: (e: AuditEvent) => e.at, actor: (e: AuditEvent) => e.actor.name, action: (e: AuditEvent) => e.action, outcome: (e: AuditEvent) => e.outcome }[sort ?? 'at'];
      const sign = (direction ?? (sort ? 'asc' : 'desc')) === 'asc' ? 1 : -1;
      items = [...items].sort((a, b) => sign * key(a).localeCompare(key(b)));
      return { items: items.slice((page - 1) * pageSize, page * pageSize), total: items.length, page, pageSize };
    },
    async exportAudit(org, format, p): Promise<ExportResult> {
      const res = await raw('GET', `${o(org)}/audit/export${qs({ ...p, format })}`, undefined, '*/*');
      return {
        filename: `audit.${format}`,
        mimeType: res.headers.get('Content-Type') ?? 'text/plain',
        content: await res.text(),
      };
    },
    listSessions: (org, p) => call('GET', `${o(org)}/sessions${qs(p)}`),
    getSession: (org, id) => call('GET', `${o(org)}/sessions/${id}`),
    accessRecording: (org, id, reason) => call('POST', `${o(org)}/sessions/${id}/recording/access`, { reason }),
    getRatingSummary: (org) => call('GET', `${o(org)}/ratings/summary`),
    getTechnicianRatings: (org, id, months) => call('GET', `${o(org)}/ratings/technicians/${encodeURIComponent(id)}${qs({ months })}`),
    listRatings: (org, p) => call('GET', `${o(org)}/ratings${qs(p)}`),

    listEnrollmentTokens: (org) => call('GET', `${o(org)}/enrollment-tokens`),
    createEnrollmentToken: (org, req) => call('POST', `${o(org)}/enrollment-tokens`, req),
    revokeEnrollmentToken: (org, id) => call('DELETE', `${o(org)}/enrollment-tokens/${id}`),
    listReleases: async (org) => (await call<{ items: Release[] }>('GET', `${o(org)}/releases`)).items,
    getLatestAgent: async (org) => (await call<{ latestAgent: string | null }>('GET', `${o(org)}/releases`)).latestAgent,

    listPlans: async () => (await call<{ items: ConsolePlanRaw[] }>('GET', '/v1/plans')).items.map(mapPlan),
    getSubscription: (org) => call('GET', `${o(org)}/billing/subscription`),
    startTrial: async (org) => {
      await call('POST', `${o(org)}/trial`);
      return call('GET', `${o(org)}/billing/subscription`);
    },
    updateSubscription: (org, patch) => call('PATCH', `${o(org)}/billing/subscription`, patch),
    listInvoices: (org) => call('GET', `${o(org)}/billing/invoices`),
    listPaymentMethods: async (org) => (await call<{ items: PaymentMethod[] }>('GET', `${o(org)}/billing/payment-methods`)).items,
    addPaymentMethod: (org, req) => call('POST', `${o(org)}/billing/payment-methods`, req),
    removePaymentMethod: (org, id) => call('DELETE', `${o(org)}/billing/payment-methods/${id}`),
    setDefaultPaymentMethod: (org, id) => call('POST', `${o(org)}/billing/payment-methods/${id}/default`),
    purchaseAiCredits: (org, req) => call('POST', `${o(org)}/billing/ai-credits`, req),
    retryPayment: (org) => call('POST', `${o(org)}/billing/retry-payment`),

    listIntegrations: (org) => call('GET', `${o(org)}/integrations`),
    setIntegrationConnected: (org, kind, connected) =>
      call('PUT', `${o(org)}/integrations/${kind}`, { connected }),
    listApiKeys: (org) => call('GET', `${o(org)}/api-keys`),
    createApiKey: (org, req) => call('POST', `${o(org)}/api-keys`, req),
    revokeApiKey: (org, id) => call('DELETE', `${o(org)}/api-keys/${id}`),
  };
}
